// Excel експортът на „По сервитьори" — списъкът и детайла по дни.
//
// Колоните за методи се генерират от СЪЩИЯ `methodEntries`, който прави
// колоните на таблицата: файлът повтаря екрана — всички активни методи, със
// смесеното разпределено в „В брой" и „С карта". Числата остават ЧИСЛА.
//
// БЕЗ собствен времеви печат — `exportToExcel` вече лепи `_${stamp}`. Редовете
// идват ФИЛТРИРАНИ и ПОДРЕДЕНИ от таблицата (`onFilteredRowsChange`).

import { exportToExcel, type ExcelColumn } from "../../../../utils/excelExport";
import type { MethodEntry } from "../../../../utils/paymentMethodsDisplay";
import type { WaiterDayRow, WaiterRow } from "./waitersMath";

/** Excel не форматира вместо нас — сумите влизат закръглени до стотинка. */
const money = (value: number) => Number(value.toFixed(2));

type Context = {
  /** Втори аргумент за ключовете с параметър (`{{method}}`). */
  t: (key: string, options?: Record<string, unknown>) => string;
  methodEntries: MethodEntry[];
};

export const exportWaiters = ({
  rows,
  methodEntries,
  showDiscounts,
  showVoids,
  t,
}: Context & {
  rows: WaiterRow[];
  showDiscounts: boolean;
  showVoids: boolean;
}) => {
  const columns: ExcelColumn<WaiterRow>[] = [
    { header: t("reportsPage.waiter"), value: (row) => row.waiterName, width: 24 },
    { header: t("reportsPage.bills"), value: (row) => row.bills, width: 10 },
    {
      header: t("reportsPage.waitersAvgBill"),
      value: (row) => money(row.average),
      width: 14,
    },
    ...methodEntries.map<ExcelColumn<WaiterRow>>((method) => ({
      header: method.label,
      value: (row) => money(row.byMethod[method.id] ?? 0),
      width: 14,
    })),
    ...(showDiscounts
      ? [
          {
            header: t("reportsPage.discountsReport"),
            value: (row: WaiterRow) => money(row.discounts),
            width: 14,
          },
          {
            header: t("reportsPage.surchargesKicker"),
            value: (row: WaiterRow) => money(row.surcharges),
            width: 14,
          },
        ]
      : []),
    ...(showVoids
      ? [
          {
            header: t("reportsPage.cancellations"),
            value: (row: WaiterRow) => money(row.voids),
            width: 14,
          },
        ]
      : []),
    {
      header: t("reportsPage.waitersRevenue"),
      value: (row) => money(row.total),
      width: 14,
    },
    {
      header: t("reportsPage.waitersShare"),
      value: (row) => Number(row.share.toFixed(2)),
      width: 10,
    },
  ];

  exportToExcel<WaiterRow>({
    filename: t("reportsPage.waitersTitle"),
    sheetName: t("reportsPage.waitersReport"),
    columns,
    rows,
  });
};

export const exportWaiterDays = ({
  rows,
  methodEntries,
  waiterName,
  t,
}: Context & { rows: WaiterDayRow[]; waiterName: string }) => {
  const columns: ExcelColumn<WaiterDayRow>[] = [
    { header: t("reportsPage.date"), value: (row) => row.dateLabel, width: 14 },
    { header: t("reportsPage.bills"), value: (row) => row.bills, width: 10 },
    {
      header: t("reportsPage.waitersAvgBill"),
      value: (row) => money(row.average),
      width: 14,
    },
    ...methodEntries.map<ExcelColumn<WaiterDayRow>>((method) => ({
      header: method.label,
      value: (row) => money(row.byMethod[method.id] ?? 0),
      width: 14,
    })),
    {
      header: t("reportsPage.waitersRevenue"),
      value: (row) => money(row.total),
      width: 14,
    },
  ];

  exportToExcel<WaiterDayRow>({
    // Името на сервитьора е В ИМЕТО НА ФАЙЛА — иначе три изтеглени детайла в
    // папката „Изтегляния" се различават само по минутата в печата.
    filename: `${t("reportsPage.waitersTitle")}_${waiterName}`,
    sheetName: t("reportsPage.waitersDetailTitle"),
    columns,
    rows,
  });
};
